import { useEffect, useRef, useState } from "react";
import { useDashboardUI } from "../context/GlobalContext";
import { userSignOut } from "../helpers/helpers";
import { refreshAccessToken } from "../helpers/axios";
import { AUTO_LOGOFF_POPUP_COUNTDOWN } from "../helpers/constant";

export default function AutoLogOffModal({ show = false }) {
  const { setShowForcedLogout } = useDashboardUI();

  const [seconds, setSeconds] = useState(AUTO_LOGOFF_POPUP_COUNTDOWN);

  const intervalRef = useRef(null);
  const expiryTimeRef = useRef(null);
  const signoutTriggeredRef = useRef(false);

  const clearTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handleLogoutNow = async () => {
    if (signoutTriggeredRef.current) return;
    signoutTriggeredRef.current = true;
    clearTimer();
    await userSignOut();
  };

  // 🔒 Prevent Escape key
  useEffect(() => {
    if (!show) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [show]);

  // ✅ Background-safe countdown
  useEffect(() => {
    if (!show) {
      clearTimer();
      expiryTimeRef.current = null;
      return;
    }

    signoutTriggeredRef.current = false;

    // ⏱️ Absolute expiry time
    expiryTimeRef.current =
      Date.now() + AUTO_LOGOFF_POPUP_COUNTDOWN * 1000;

    const tick = () => {
      if (!expiryTimeRef.current) return;

      const remainingMs = expiryTimeRef.current - Date.now();
      const remainingSeconds = Math.max(
        Math.ceil(remainingMs / 1000),
        0
      );

      setSeconds(remainingSeconds);

      if (remainingSeconds <= 0) {
        clearTimer();
        setShowForcedLogout(false);
        handleLogoutNow();
      }
    };

    tick(); // run immediately
    intervalRef.current = setInterval(tick, 1000);

    return () => clearTimer();
  }, [show, setShowForcedLogout]);

  if (!show) return null;

  const stop = (e) => e.stopPropagation();

  const onKeepLoggedIn = async () => {
    try {
      const token = await refreshAccessToken();

      if (!token) {
        handleLogoutNow();
        return;
      }

      clearTimer();
      setShowForcedLogout(false);
    } catch (error) {
      console.error("Error refreshing user session:", error);
      handleLogoutNow();
    }
  };

    return (
        <div
            className="
                fixed inset-0 bg-black/60
                flex items-center justify-center
                z-[9999]
            "
            aria-modal="true"
            role="dialog"
            onClick={stop}
        >
            <div
                className="
                    w-[420px] max-w-[90%]
                    bg-white rounded-lg p-6
                    shadow-xl text-center
                "
                onClick={stop}
            >
                <h2 className="m-0 text-xl font-bold text-black">
                    Session Expired
                </h2>

                <p className="mt-3 text-gray-700 text-sm">
                    Oops! Your session has expired!
                </p>

                <p className="mt-2 text-gray-600 text-xs italic">
                    Redirecting to login page in {seconds} second
                    {seconds !== 1 ? "s" : ""}...
                </p>

                {/* Actions */}
                <div className="mt-6 flex gap-3 justify-center">

                    <button
                        type="button"
                        onClick={handleLogoutNow}
                        className="flex items-center gap-2 rounded-xl bg-gray-200 px-6 h-10 text-sm font-medium text-black hover:bg-gray-300 transition"
                    >
                        Log out
                    </button>

                    <button
                        type="button"
                        onClick={onKeepLoggedIn}
                        className="flex items-center gap-2 rounded-xl bg-blue-500 px-6 h-10 text-sm font-medium text-white"
                    >
                        Keep me logged in
                    </button>
                </div>
            </div>
        </div>
    );
}
